import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { GetStaticProps } from 'next';
import SbEditable from 'storyblok-react';

import { Block, Heading, Seo } from '~/components';
import { BannerDynamic } from '~/components/Banners/BannerDynamic';
import {
  EventCard,
  HighlightBanner,
  OverviewHeader,
} from '~/components/Webinars';
import { WebinarItems, WebinarrecordedItems } from '~/graphql/sdk';

import {
  enhancedStaticProps,
  withLayout,
  addFormFieldsToStory,
  getSlugWithLocale,
} from '~/lib';

export interface WebinarOverviewProps extends WithStoryProps {
  upcomingWebinars?: WebinarItems;
  recordedWebinars?: WebinarrecordedItems;
}

const WebinarOverview = ({
  story,
  upcomingWebinars,
  recordedWebinars,
}: WebinarOverviewProps) => {
  const content = story?.content;

  return (
    <div className="pt-12 lg:pt-32">
      <Seo story={story} />

      <SbEditable content={content}>
        <OverviewHeader content={content} />

        {upcomingWebinars?.items?.length > 0 && (
          <div id="webinars" className="w-full max-w-6xl px-4 mx-auto">
            <Heading variant="h4" as="h4">
              {content?.upcoming_title || 'Upcoming live events'}
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 mt-10 gap-12 lg:gap-16">
              {upcomingWebinars?.items?.map((webinar) => (
                <EventCard
                  key={webinar?.uuid}
                  event={webinar}
                  cta={content?.upcoming_cta}
                />
              ))}
            </div>
          </div>
        )}

        {!!content?.highlight?.content && (
          <HighlightBanner
            url={content?.highlight?.fullSlug}
            content={content?.highlight?.content}
            image={content?.highlight_image}
            subtext={content?.highlight_subtext}
            cta={content?.highlight_cta}
          />
        )}

        {recordedWebinars?.items?.length > 0 && (
          <div className="w-full max-w-6xl px-4 mx-auto py-20">
            <Heading variant="h4" as="h4">
              {content?.recorded_title || 'Learn in your own pace'}
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 mt-10 gap-12 lg:gap-16">
              {recordedWebinars?.items?.map((webinar) => (
                <EventCard
                  key={webinar?.uuid}
                  event={webinar}
                  cta={content?.upcoming_cta}
                  video
                />
              ))}
            </div>
          </div>
        )}
      </SbEditable>

      {!!story?.content?.body?.[0]?.component &&
        story?.content?.body?.map((section, i) => {
          return (
            <Block
              component={story?.content?.component}
              sectionType={section?.component}
              content={section}
              first={i === 0}
              key={section?._uid}
              title={story?.content?.seo?.title || story?.content?.title}
            />
          );
        })}

      {!!story?.content?.banner?.content && (
        <SbEditable content={story?.content?.banner?.content}>
          <div className="w-full bg-gradient-to-b from-[#FDF7DF] to-white mt-52">
            <div className="w-full max-w-6xl mx-auto px-4 md:px-5 pt-0 relative -top-32">
              <BannerDynamic content={story?.content?.banner?.content} />
            </div>
          </div>
        </SbEditable>
      )}
    </div>
  );
};

export default withLayout(withStory(WebinarOverview));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ sdk, locale, defaultLocale }) => {
    let story;
    let upcomingWebinars;
    let recordedWebinars;
    let notFound = false;

    try {
      story = (
        await sdk.webinarOverviewItem({
          slug: getSlugWithLocale(`webinars`, locale, defaultLocale),
          lang: locale,
        })
      ).WebinaroverviewItem;
      story = await addFormFieldsToStory(story);
    } catch (e) {
      notFound = true;
    }

    try {
      upcomingWebinars = (
        await sdk.webinarItems({
          starts_with: locale === defaultLocale ? '' : `${locale}/*`,
          lang: locale,
        })
      ).WebinarItems;
    } catch (e) {
      console.log(e);
    }

    try {
      recordedWebinars = (
        await sdk.webinarRecordedItems({
          starts_with: locale === defaultLocale ? '' : `${locale}/*`,
          lang: locale,
        })
      ).WebinarrecordedItems;
    } catch (e) {
      console.log(e);
    }

    return {
      props: {
        story,
        upcomingWebinars,
        recordedWebinars,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);
